import type { QueuedMutation } from './offlineQueue';
import { PENDING_SEGMENT_ID_PREFIX } from './pendingSegmentsFromQueue';

/**
 * Traducción del id de un tramo creado sin cobertura, al reproducir la cola.
 *
 * ─── El problema que resuelve ──────────────────────────────────────────────
 *
 * Un tramo iniciado sin red **no tiene id del servidor**: mientras espera en la
 * cola se le da uno sintético (`pending-<id de la operación>`) para poder
 * pintarlo. Si el operario lo cierra o cambia de sentido antes de recuperar
 * cobertura —lo normal en carretera, donde puede tardar horas—, esas
 * operaciones se encolan **apuntando a ese id inventado**, que el backend no
 * conoce.
 *
 * La alternativa era prohibirle cerrar hasta que hubiera señal. Inaceptable:
 * el tramo se quedaría abierto, el siguiente no podría empezar, y todo lo que
 * hiciera entre medias se perdería.
 *
 * ─── Cómo se resuelve ──────────────────────────────────────────────────────
 *
 * Cuando el `CreateRouteSegment` se reproduce con éxito, el servidor devuelve
 * el id de verdad. En ese momento se sustituye el sintético por el real en TODO
 * lo que quede en la cola. Es el patrón clásico de las colas offline y no toca
 * el backend: la cola es nuestra.
 *
 * La sustitución es **profunda y por valor exacto**, no por campo conocido: hoy
 * el id viaja en `input.id` (finalizar, corregir, causa de pausa), pero una
 * mutation futura podría meterlo en otro sitio y el fallo sería silencioso —
 * una operación encolada apuntando a un id que no existe, descartada al
 * sincronizar sin que nadie se entere.
 */

/** Id sintético que `mergePendingSegments` dio al tramo de esta operación. */
export function syntheticIdFor(queuedMutationId: string): string {
  return `${PENDING_SEGMENT_ID_PREFIX}${queuedMutationId}`;
}

/**
 * El id que el servidor acaba de asignar al tramo, si esta operación era la
 * creación de uno. `null` para cualquier otra cosa.
 */
export function createdSegmentIdFrom(
  operationName: string,
  data: unknown
): string | null {
  if (operationName !== 'CreateRouteSegment') return null;
  const created = (data as { createRouteSegment?: { id?: string } } | null | undefined)
    ?.createRouteSegment;
  return typeof created?.id === 'string' && created.id ? created.id : null;
}

/** Sustituye `fromId` por `toId` en cualquier punto de la estructura. */
function deepReplace<T>(value: T, fromId: string, toId: string): T {
  if (typeof value === 'string') {
    return (value === fromId ? toId : value) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => deepReplace(item, fromId, toId)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      out[key] = deepReplace(item, fromId, toId);
    }
    return out as unknown as T;
  }
  return value;
}

/** ¿Esta operación menciona el id sintético en alguna parte de sus variables? */
export function referencesSyntheticId(
  mutation: QueuedMutation,
  syntheticId: string
): boolean {
  return JSON.stringify(mutation.variables ?? {}).includes(`"${syntheticId}"`);
}

/**
 * Devuelve las operaciones cuyas variables cambian, ya reescritas. Las que no
 * mencionan el id sintético se quedan fuera: así el llamante solo persiste lo
 * que de verdad ha cambiado.
 */
export function remapQueuedMutations(
  queue: QueuedMutation[],
  syntheticId: string,
  realId: string
): QueuedMutation[] {
  if (!syntheticId || !realId || syntheticId === realId) return [];
  return queue
    .filter((mutation) => referencesSyntheticId(mutation, syntheticId))
    .map((mutation) => ({
      ...mutation,
      variables: deepReplace(mutation.variables, syntheticId, realId),
    }));
}
